import { Navigate } from 'react-router-dom'

import { PreloadableRouteObject, TFileToRouteMap } from '~/app/models'
import { getRoutesFromMap } from '~/app/utils/lazy'
import ProtectedFromPartnerRouteGuard from '~/features/partners/components/protectedFromPartnerRouteGuard'
import { EClientPath } from '~/models/api'
import CampaignCreateEditContainer from '~/pages/campaigns/createEdit'

const contactDetailsImport = () =>
  import('~/features/contacts/components/contactDetails')
const inboxImport = () => import('~/pages/inbox')

const basicAccountPortalRoutesFileToRouteMap: TFileToRouteMap = {
  overview: {
    path: EClientPath.overview,
    getImportPromise: () => import('~/pages/overview'),
  },

  basicInfo: {
    path: EClientPath.settingsBasicInfo,
    getImportPromise: () => import('~/features/settings/components/basicInfo'),
  },
  creatingLibby: {
    path: EClientPath.settingsCreatingLibby,
    getImportPromise: () =>
      import('~/features/settings/components/creatingLibby'),
  },
  convoOptions: {
    path: EClientPath.settingsConvoOptions,
    getImportPromise: () =>
      import('~/features/settings/components/convoOptions'),
  },
  knowledge: {
    path: EClientPath.settingsKnowledge,
    getImportPromise: () => import('~/features/knowledge'),
  },
  individualKnowledgeSet: {
    path: EClientPath.settingsIndividualKnowledgeSet,
    getImportPromise: () =>
      import('~/features/knowledge/v2/components/knowledgeSet'),
  },
  chatbot: {
    path: EClientPath.settingsChatbot,
    getImportPromise: () => import('~/features/settings/components/chatBot'),
  },
  email: {
    path: EClientPath.settingsEmail,
    getImportPromise: () => import('~/features/settings/components/email'),
  },
  phoneText: {
    path: EClientPath.settingsPhoneText,
    getImportPromise: () => import('~/features/settings/components/phoneText'),
  },
  notifications: {
    path: EClientPath.settingsNotifications,
    getImportPromise: () =>
      import('~/features/settings/components/notifications'),
  },
  escalations: {
    path: EClientPath.settingsEscalations,
    getImportPromise: () =>
      import('~/features/settings/components/escalations'),
  },
  inbox: {
    path: EClientPath.inbox,
    getImportPromise: inboxImport,
  },
  inboxMessage: {
    path: EClientPath.inboxMessage,
    getImportPromise: inboxImport,
  },
  contacts: {
    path: EClientPath.contacts,
    getImportPromise: () => import('~/pages/contacts'),
  },
  contactDetails: {
    path: EClientPath.contact,
    getImportPromise: contactDetailsImport,
  },
  contactConversation: {
    path: EClientPath.contactConversation,
    getImportPromise: contactDetailsImport,
  },
  reports: {
    path: EClientPath.reports,
    getImportPromise: () => import('~/pages/reports'),
  },
  accountSupport: {
    path: EClientPath.support,
    getImportPromise: () => import('~/pages/accountSupport'),
  },
  partnerBilling: {
    path: EClientPath.account,
    getImportPromise: () =>
      import('~/features/partners/components/partnerBilling'),
  },
  integrations: {
    path: EClientPath.integrations,
    getImportPromise: () => import('~/pages/integrations'),
  },
  privacyPolicy: {
    path: EClientPath.privacyPolicy,
    getImportPromise: () => import('~/pages/privacyPolicy'),
  },
  termsOfUse: {
    path: EClientPath.termsOfUse,
    getImportPromise: () => import('~/pages/termsOfUse'),
  },
  referralsTerms: {
    path: EClientPath.referralsTerms,
    getImportPromise: () => import('~/pages/referrals/referralsTerms'),
  },
  campaignsList: {
    path: EClientPath.campaignList,
    getImportPromise: () => import('~/pages/campaigns/home'),
  },
  manageAssistants: {
    path: EClientPath.manageAssistants,
    getImportPromise: () => import('~/pages/manageAssistants'),
  },
  manageScenarios: {
    path: EClientPath.manageScenarios,
    getImportPromise: () => import('~/pages/scenarios/manageScenarios'),
  },
  scenarioDetail: {
    path: EClientPath.scenarioCreateEdit,
    getImportPromise: () => import('~/pages/scenarios/createEditScenario'),
  },
  users: {
    path: EClientPath.users,
    getImportPromise: () => import('~/pages/users'),
  },
  scenarioActionDetail: {
    path: EClientPath.scenarioActionCreateEdit,
    getImportPromise: () =>
      import('~/pages/scenarios/createEditScenarioAction'),
  },
  liveTransfers: {
    path: EClientPath.liveTransfersScenarios,
    getImportPromise: () => import('~/pages/liveTransfers'),
  },
  tcrForm: {
    path: EClientPath.tcrForm,
    getImportPromise: () => import('~/pages/tcrForm'),
  },
}

const campaignChildRoutesFileToRouteMap: TFileToRouteMap = {
  campaignDetails: {
    path: EClientPath.campaignDetails,
    getImportPromise: () => import('~/features/campaigns/components/details'),
  },
  campaignContacts: {
    path: EClientPath.campaignContacts,
    getImportPromise: () => import('~/features/campaigns/components/contacts'),
  },
  campaignResults: {
    path: EClientPath.campaignResults,
    getImportPromise: () => import('~/features/campaigns/components/results'),
  },
}

const protectedFromPartnerRoutesFileToRouteMap: TFileToRouteMap = {
  billing: {
    path: EClientPath.billing,
    getImportPromise: () => import('~/pages/billing'),
  },
  referralsInfo: {
    path: EClientPath.referralsInfo,
    getImportPromise: () => import('~/pages/referrals/referralsInfo'),
  },
}

const accountPortalRoutes: PreloadableRouteObject[] = getRoutesFromMap(
  basicAccountPortalRoutesFileToRouteMap,
)
  .concat({
    element: <ProtectedFromPartnerRouteGuard />,
    children: getRoutesFromMap(protectedFromPartnerRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <CampaignCreateEditContainer />,
    path: EClientPath.campaignCreateEdit,
    children: getRoutesFromMap(campaignChildRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    path: EClientPath.settingsBase,
    element: <Navigate to={EClientPath.settingsBasicInfo} replace />,
  } as PreloadableRouteObject)

export default accountPortalRoutes
